import { useEffect, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Field } from "./common";
import { api, errMsg } from "../lib/api";
import { rp, todayISO } from "../lib/format";

const useExitInfo = (anak, open) => {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    if (!open || !anak) return;
    setInfo(null);
    api.get(`/anak/${anak.id}/keluar-info`).then((r) => setInfo(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [open, anak]);
  return info;
};

const Warn = ({ children, testid }) => (
  <div data-testid={testid} className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /><div>{children}</div>
  </div>
);

const Warnings = ({ info, unpaid }) => (
  <div className="space-y-2">
    {info.sisa_kasbon > 0 && <Warn testid="exit-warn-kasbon">Anak ini masih punya sisa kasbon <b className="font-mono">{rp(info.sisa_kasbon)}</b>. Lanjutkan?</Warn>}
    {info.peralatan_dipinjam.length > 0 && <Warn testid="exit-warn-peralatan">Masih meminjam: <b>{info.peralatan_dipinjam.join(", ")}</b></Warn>}
    {unpaid && info.voucher_belum_dibayar + info.kasbon_belum_dibayar > 0 && (
      <Warn testid="exit-warn-unpaid">Ada {info.voucher_belum_dibayar} voucher dan {info.kasbon_belum_dibayar} kasbon di periode yang <b>belum dibayar</b>. Angka gajian periode tersebut akan hilang.</Warn>
    )}
  </div>
);

export const TandaiKeluarDialog = ({ anak, open, onOpenChange, onDone }) => {
  const info = useExitInfo(anak, open);
  const [tanggal, setTanggal] = useState(todayISO());
  const [alasan, setAlasan] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setTanggal(todayISO()); setAlasan(""); } }, [open]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/anak/${anak.id}/keluar`, { tanggal_keluar: tanggal, alasan: alasan || null });
      toast.success(`${anak.nama} ditandai keluar`);
      onOpenChange(false);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="keluar-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Tandai keluar: {anak?.nama}</DialogTitle>
          <DialogDescription>Anak akan dipindah ke Arsip, tidak muncul di daftar aktif, dan tidak bisa login portal. Riwayat tetap tersimpan.</DialogDescription>
        </DialogHeader>
        {!info ? <p className="text-sm text-gray-500">Memeriksa data…</p> : <Warnings info={info} />}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Tanggal keluar"><Input type="date" data-testid="keluar-tanggal" className="h-10" value={tanggal} onChange={(e) => setTanggal(e.target.value)} /></Field>
          <Field label="Alasan (opsional)"><Input data-testid="keluar-alasan" className="h-10" value={alasan} onChange={(e) => setAlasan(e.target.value)} /></Field>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" className="h-10" data-testid="keluar-cancel" onClick={() => onOpenChange(false)}>Batal</Button>
          <Button data-testid="keluar-confirm" disabled={!info || busy || !tanggal} className="h-10 bg-amber-600 hover:bg-amber-700" onClick={submit}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Ya, tandai keluar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const HapusPermanenDialog = ({ anak, open, onOpenChange, onDone }) => {
  const info = useExitInfo(anak, open);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.delete(`/anak/${anak.id}`);
      toast.success(`${anak.nama} dihapus permanen`);
      onOpenChange(false);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="hapus-permanen-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-red-700">Hapus {anak?.nama} permanen?</DialogTitle>
          <DialogDescription>Menghapus anak beserta semua voucher, kasbon, kehadiran, peralatan, foto wajah & akun login. Tidak bisa dibatalkan. Rekap gajian yang sudah dibayar tetap utuh.</DialogDescription>
        </DialogHeader>
        {!info ? <p className="text-sm text-gray-500">Memeriksa data…</p> : <Warnings info={info} unpaid />}
        <DialogFooter className="gap-2">
          <Button variant="outline" className="h-10" data-testid="hapus-permanen-cancel" onClick={() => onOpenChange(false)}>Batal</Button>
          <Button data-testid="hapus-permanen-confirm" disabled={!info || busy} className="h-10 bg-red-600 hover:bg-red-700" onClick={submit}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Hapus
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
